import { Controller } from "@hotwired/stimulus"
import Lenis from 'lenis';

export default class extends Controller {
  static values = {
    duration: { type: Number, default: 0.6 },
    smooth: { type: Boolean, default: true },
    smoothTouch: { type: Boolean, default: false }
  }

  connect() {
    this.initLenis()
    this.bindEvents()
  }

  disconnect() {
    this.destroyLenis()
    this.unbindEvents()
  }

  initLenis() {
    this.lenis = new Lenis({
      duration: this.durationValue, // Faster duration: 0.6s instead of 1.2s
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: this.smoothValue,
      smoothTouch: this.smoothTouchValue,
      mouseMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    })

    // Store reference globally for other controllers
    window.lenis = this.lenis

    this.startRaf()
  }

  startRaf() {
    const raf = (time) => {
      this.lenis?.raf(time)
      requestAnimationFrame(raf)
    }
    requestAnimationFrame(raf)
  }

  bindEvents() {
    // Handle Turbo events for page transitions
    document.addEventListener('turbo:before-cache', this.handleBeforeCache.bind(this))
    document.addEventListener('turbo:load', this.handleTurboLoad.bind(this))
  }

  unbindEvents() {
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache.bind(this))
    document.removeEventListener('turbo:load', this.handleTurboLoad.bind(this))
  }

  handleBeforeCache() {
    if (this.lenis) {
      this.lenis.destroy()
    }
  }

  handleTurboLoad() {
    // Reinitialize after Turbo page load
    this.initLenis()
  }

  // Public method for programmatic scrolling
  scrollTo(target, options = {}) {
    if (this.lenis) {
      this.lenis.scrollTo(target, {
        duration: options.duration || this.durationValue,
        easing: options.easing || ((t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))),
        ...options
      })
    }
  }

  destroyLenis() {
    if (this.lenis) {
      this.lenis.destroy()
      this.lenis = null
    }
    if (window.lenis === this.lenis) {
      delete window.lenis
    }
  }
}
